import { useState, FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Shield, FileText, Trash2, Download, Ban, UserX } from 'lucide-react';

const DataRights = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    requestType: 'access',
    fullName: '',
    email: '',
    phone: '',
    idNumber: '',
    details: ''
  });

  const requestTypes = [
    {
      value: 'access',
      label: 'זכות עיון - בקשה לקבלת עותק מהמידע האישי שלי',
      icon: FileText,
      description: 'קבל עותק של כל המידע האישי שבידינו'
    },
    {
      value: 'correction',
      label: 'תיקון מידע - בקשה לתיקון מידע שגוי',
      icon: FileText,
      description: 'תקן פרטים שגויים או לא מדויקים'
    },
    {
      value: 'deletion',
      label: 'זכות למחיקה - בקשה למחיקת המידע שלי',
      icon: Trash2,
      description: 'מחק את כל המידע האישי שלך ממערכותינו'
    },
    {
      value: 'portability',
      label: 'זכות להעברה - בקשה לקבלת המידע בפורמט מובנה',
      icon: Download,
      description: 'קבל את המידע שלך בפורמט נפוץ (JSON/CSV)'
    },
    {
      value: 'objection',
      label: 'זכות להתנגד - התנגדות לעיבוד מסוים',
      icon: Ban,
      description: 'התנגד לשימוש מסוים במידע שלך'
    },
    {
      value: 'withdraw',
      label: 'ביטול הסכמה - ביטול הסכמה שניתנה בעבר',
      icon: UserX,
      description: 'בטל הסכמה לשימוש במידע שלך'
    }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate required fields
    if (!formData.fullName || !formData.email || !formData.idNumber) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא את כל השדות הנדרשים',
        variant: 'destructive'
      });
      setIsSubmitting(false);
      return;
    }

    // Simulate form submission
    setTimeout(() => {
      toast({
        title: 'הבקשה נשלחה בהצלחה',
        description: 'תקבל מענה תוך 30 יום כנדרש בחוק. נשלח אליך אישור למייל.',
      });
      
      setFormData({
        requestType: 'access',
        fullName: '',
        email: '',
        phone: '',
        idNumber: '',
        details: ''
      });
      
      setIsSubmitting(false);
    }, 1500);
  };

  const selectedRequestType = requestTypes.find(type => type.value === formData.requestType);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-20 sm:py-24">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">זכויות נפגע - הגנת הפרטיות</h1>
            <p className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              בהתאם לחוק הגנת הפרטיות (תיקון 13), התשפ"ה-2025, יש לך מספר זכויות בנוגע למידע האישי שלך.
              השתמש בטופס זה כדי להפעיל את זכויותיך.
            </p>
          </div>

          {/* Info Card */}
          <Card className="mb-6 sm:mb-8 border-primary/20">
            <CardHeader className="pb-3 sm:pb-6">
              <CardTitle className="text-base sm:text-lg">מידע חשוב</CardTitle>
              <CardDescription className="text-sm">לפני שליחת הבקשה</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-muted-foreground">
              <ul className="list-disc pr-5 sm:pr-6 space-y-2">
                <li>נענה לבקשתך תוך <strong className="text-foreground">30 יום</strong> ממועד קבלתה</li>
                <li>תקבל אישור קבלה למייל שסיפקת</li>
                <li>נדרוש אימות זהות להגנה על פרטיותך</li>
                <li>חלק מהבקשות עשויות להיות כפופות לחריגים חוקיים (למשל, שמירת מסמכים משפטיים)</li>
                <li>הבקשה היא ללא עלות</li>
              </ul>
            </CardContent>
          </Card>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
            {/* Request Type Selection */}
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="text-base sm:text-lg">סוג הבקשה *</CardTitle>
                <CardDescription className="text-sm">בחר את סוג הבקשה שברצונך להגיש</CardDescription>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={formData.requestType}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, requestType: value }))}
                  className="space-y-3"
                >
                  {requestTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <div
                        key={type.value}
                        className="flex items-start gap-3 border rounded-lg p-3 sm:p-4 hover:bg-accent/50 transition-colors cursor-pointer"
                        onClick={() => setFormData(prev => ({ ...prev, requestType: type.value }))}
                      >
                        <RadioGroupItem value={type.value} id={type.value} className="mt-1 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <label
                            htmlFor={type.value}
                            className="flex items-center gap-2 text-sm sm:text-base font-medium cursor-pointer mb-1"
                          >
                            <Icon className="h-4 w-4 text-primary" />
                            {type.label}
                          </label>
                          <p className="text-sm text-muted-foreground">{type.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Personal Information */}
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="text-base sm:text-lg">פרטים אישיים</CardTitle>
                <CardDescription className="text-sm">מידע זה נדרש לצורך אימות הזהות שלך</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-sm">שם מלא *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      placeholder="שם פרטי ושם משפחה"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="idNumber" className="text-sm">מספר תעודת זהות *</Label>
                    <Input
                      id="idNumber"
                      name="idNumber"
                      value={formData.idNumber}
                      onChange={handleChange}
                      required
                      placeholder="9 ספרות"
                      maxLength={9}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm">אימייל *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="example@mail.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm">טלפון</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="05X-XXXXXXX"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Details */}
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="text-base sm:text-lg">פרטים נוספים</CardTitle>
                <CardDescription className="text-sm">
                  {selectedRequestType?.value === 'correction' && 'פרט איזה מידע יש לתקן ומה התיקון הנדרש'}
                  {selectedRequestType?.value === 'objection' && 'פרט לאיזה עיבוד אתה מתנגד ומדוע'}
                  {selectedRequestType?.value === 'withdraw' && 'פרט לאיזו הסכמה אתה מבקש לבטל'}
                  {!['correction', 'objection', 'withdraw'].includes(selectedRequestType?.value || '') && 
                    'הוסף כל מידע נוסף שיכול לעזור לנו לטפל בבקשתך (אופציונלי)'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  id="details"
                  name="details"
                  value={formData.details}
                  onChange={handleChange}
                  placeholder="הוסף פרטים נוספים..."
                  className="min-h-[120px]"
                />
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex justify-center">
              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                className="px-8"
              >
                {isSubmitting ? 'שולח בקשה...' : 'שלח בקשה'}
              </Button>
            </div>
          </form>

          {/* Footer Info */}
          <div className="mt-8 sm:mt-12 p-4 sm:p-6 bg-muted/30 rounded-lg border text-xs sm:text-sm text-muted-foreground">
            <p className="font-semibold text-foreground mb-2">שאלות או בעיות?</p>
            <p className="mb-3">
              אם יש לך שאלות לגבי זכויותיך או אם נתקלת בבעיה בהגשת הבקשה, אנא צור איתנו קשר:
            </p>
            <div className="space-y-1">
              <p>אימייל: <a href="mailto:office@szlaw.co.il" className="text-primary hover:underline">office@szlaw.co.il</a></p>
              <p>טלפון: <a href="tel:+97231234567" className="text-primary hover:underline">03-123-4567</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataRights;